import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { financialsApi } from '@/lib/financialsApi';
import { toNumber } from '@/lib/number';
import { Card } from '@/components/ui/Card';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  ChevronRight,
  BarChart3,
} from 'lucide-react';
import type { ApiResponse } from '@/types/api';
import type { FarmFinancialSummary } from '@/types/financials';

interface FinancialSummaryWidgetProps {
  farmId: string;
}

function formatPHP(value: number): string {
  return value.toLocaleString('en-PH', {
    style: 'currency',
    currency: 'PHP',
    maximumFractionDigits: 0,
  });
}

export function FinancialSummaryWidget({ farmId }: FinancialSummaryWidgetProps) {
  const { data, isLoading, error } = useQuery<ApiResponse<FarmFinancialSummary>>({
    queryKey: ['financials', 'summary', farmId],
    queryFn: () => financialsApi.getFarmSummary(farmId),
    staleTime: 2 * 60 * 1000, // 2 minutes
    enabled: !!farmId,
  });

  const summary = data?.data;

  if (isLoading) {
    return (
      <Card>
        <Card.Header>Financials</Card.Header>
        <Card.Body className="flex items-center justify-center py-8">
          <LoadingSpinner size={20} className="text-gray-400" />
        </Card.Body>
      </Card>
    );
  }

  if (error || !summary) {
    return (
      <Card>
        <Card.Header
          action={
            <Link
              to="/financials"
              className="text-sm text-primary-600 hover:text-primary-700 font-medium inline-flex items-center gap-1"
            >
              View details
              <ChevronRight className="h-3.5 w-3.5" />
            </Link>
          }
        >
          Financials
        </Card.Header>
        <Card.Body>
          <p className="text-sm text-gray-500">
            {error ? 'Unable to load financial data.' : 'No financial data available.'}
          </p>
        </Card.Body>
      </Card>
    );
  }

  const totalCosts = toNumber(summary.total_costs);
  const totalRevenue = toNumber(summary.total_revenue);
  const totalProfit = toNumber(summary.total_profit);
  const avgCostPerKg = summary.avg_cost_per_kg != null ? toNumber(summary.avg_cost_per_kg) : null;
  const isProfitable = totalProfit >= 0;

  return (
    <Card>
      <Card.Header
        action={
          <Link
            to="/financials"
            className="text-sm text-primary-600 hover:text-primary-700 font-medium inline-flex items-center gap-1"
          >
            View details
            <ChevronRight className="h-3.5 w-3.5" />
          </Link>
        }
      >
        Financials
      </Card.Header>
      <Card.Body className="space-y-4">
        {/* Compact KPI rows */}
        <div className="grid grid-cols-2 gap-4">
          <MetricRow
            icon={DollarSign}
            iconBg="bg-red-50"
            iconColor="text-red-600"
            label="Costs"
            value={formatPHP(totalCosts)}
          />
          <MetricRow
            icon={TrendingUp}
            iconBg="bg-green-50"
            iconColor="text-green-600"
            label="Revenue"
            value={formatPHP(totalRevenue)}
          />
          <MetricRow
            icon={isProfitable ? TrendingUp : TrendingDown}
            iconBg={isProfitable ? 'bg-emerald-50' : 'bg-red-50'}
            iconColor={isProfitable ? 'text-emerald-600' : 'text-red-600'}
            label="Profit"
            value={formatPHP(totalProfit)}
            valueColor={isProfitable ? 'text-emerald-700' : 'text-red-700'}
          />
          <MetricRow
            icon={BarChart3}
            iconBg="bg-blue-50"
            iconColor="text-blue-600"
            label="Avg Cost/kg"
            value={avgCostPerKg != null ? formatPHP(avgCostPerKg) : '--'}
          />
        </div>

        {/* Cycle summary */}
        <div className="flex items-center justify-between text-sm border-t border-gray-100 pt-3">
          <span className="text-gray-500">
            {summary.active_cycles} active / {summary.harvested_cycles} harvested
          </span>
          {summary.avg_roi_pct != null && (
            <span className={summary.avg_roi_pct >= 0 ? 'text-emerald-600 font-medium' : 'text-red-600 font-medium'}>
              Avg ROI: {summary.avg_roi_pct.toFixed(1)}%
            </span>
          )}
        </div>
      </Card.Body>
    </Card>
  );
}

function MetricRow({
  icon: Icon,
  iconBg,
  iconColor,
  label,
  value,
  valueColor,
}: {
  icon: typeof DollarSign;
  iconBg: string;
  iconColor: string;
  label: string;
  value: string;
  valueColor?: string;
}) {
  return (
    <div className="flex items-center gap-2.5">
      <div className={`rounded-md p-1.5 ${iconBg} shrink-0`}>
        <Icon className={`h-3.5 w-3.5 ${iconColor}`} />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-gray-400">{label}</p>
        <p className={`text-sm font-semibold truncate ${valueColor || 'text-gray-900'}`}>
          {value}
        </p>
      </div>
    </div>
  );
}
